import { stripRunningHubHistoryListForLocalStorage } from './runningHubHistoryLocalStorage';

export type RunningHubHistoryFeatureType =
  | 'upscale'
  | 'tone_mapping'
  | 'light_reconstruct'
  | 'text_reconstruct'
  | 'physical_reconstruct';

/** 未登录 guest 在 ownerEmail 与 storage key 中的占位 */
export const RUNNINGHUB_HISTORY_GUEST_SCOPE = '__guest__';

export const RUNNINGHUB_HISTORY_OWNER_EMAIL_KEY = 'ownerEmail';

/** 与后端 normalizeRunningHubHistoryUserEmail 一致 */
export function normalizeRunningHubHistoryOwnerEmail(raw: string | null | undefined): string | null {
  const s = String(raw || '').trim().toLowerCase();
  return s && s.includes('@') ? s : null;
}

export function getRunningHubHistoryOwnerScope(userEmail?: string | null): string {
  return normalizeRunningHubHistoryOwnerEmail(userEmail) ?? RUNNINGHUB_HISTORY_GUEST_SCOPE;
}

/** v4 及以前的全局 key（无用户隔离）— 仅用于删除，禁止再导入 */
export const RUNNINGHUB_HISTORY_LEGACY_BASE_KEYS: Record<RunningHubHistoryFeatureType, string> = {
  upscale: 'upscaleHistory',
  tone_mapping: 'toneMappingHistory',
  light_reconstruct: 'lightReconstructHistory',
  text_reconstruct: 'textReconstructHistory',
  physical_reconstruct: 'physicalReconstructHistory',
};

const PURGED_LEGACY_GLOBAL_KEY = 'nemo_rh_history_purged_global_v6';

/** 删除旧全局 localStorage key，禁止把无 owner 的 legacy 条目迁入登录用户 scoped key */
export function purgeLegacyGlobalRunningHubHistoryKeysOnce(): void {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
  if (localStorage.getItem(PURGED_LEGACY_GLOBAL_KEY) === '1') return;
  try {
    for (const k of Object.values(RUNNINGHUB_HISTORY_LEGACY_BASE_KEYS)) {
      localStorage.removeItem(k);
    }
    localStorage.setItem(PURGED_LEGACY_GLOBAL_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function getRunningHubHistoryStorageKey(
  featureType: RunningHubHistoryFeatureType,
  userEmail?: string | null,
): string {
  const base = RUNNINGHUB_HISTORY_LEGACY_BASE_KEYS[featureType];
  const scope = getRunningHubHistoryOwnerScope(userEmail);
  if (scope === RUNNINGHUB_HISTORY_GUEST_SCOPE) return `guest_${base}`;
  return `${base}:${scope}`;
}

/**
 * 为条目补 ownerEmail；若已有 owner 与当前会话不一致则返回 null（drop）。
 * 不覆盖已有合法 ownerEmail。
 */
export function stampRunningHubHistoryOwner<T extends Record<string, unknown>>(
  item: T,
  userEmail?: string | null,
): (T & { ownerEmail: string }) | null {
  const isGuestSession = !normalizeRunningHubHistoryOwnerEmail(userEmail);
  const expected = getRunningHubHistoryOwnerScope(userEmail);
  const raw = item[RUNNINGHUB_HISTORY_OWNER_EMAIL_KEY];

  if (typeof raw === 'string' && raw.trim()) {
    const ownerLower = raw.trim().toLowerCase();
    const ownerNorm = normalizeRunningHubHistoryOwnerEmail(raw);
    const ownerIsGuest =
      ownerLower === RUNNINGHUB_HISTORY_GUEST_SCOPE ||
      (!ownerNorm && ownerLower === RUNNINGHUB_HISTORY_GUEST_SCOPE);
    const ownerResolved = ownerNorm ?? (ownerIsGuest ? RUNNINGHUB_HISTORY_GUEST_SCOPE : null);

    if (!ownerResolved) {
      console.warn('[runninghub-history] drop invalid ownerEmail', { id: item.id, raw });
      return null;
    }

    if (!isGuestSession && ownerResolved === RUNNINGHUB_HISTORY_GUEST_SCOPE) {
      console.warn('[runninghub-history] drop guest owner in authenticated session', {
        id: item.id,
        expected,
      });
      return null;
    }

    if (isGuestSession && ownerResolved !== RUNNINGHUB_HISTORY_GUEST_SCOPE) {
      console.warn('[runninghub-history] drop non-guest owner in guest session', {
        id: item.id,
        owner: ownerResolved,
      });
      return null;
    }

    if (ownerResolved !== expected) {
      console.warn('[runninghub-history] drop owner mismatch', {
        id: item.id,
        expected,
        owner: ownerResolved,
      });
      return null;
    }

    return { ...item, [RUNNINGHUB_HISTORY_OWNER_EMAIL_KEY]: ownerResolved };
  }

  return { ...item, [RUNNINGHUB_HISTORY_OWNER_EMAIL_KEY]: expected };
}

export function runningHubHistoryItemOwnedBy(item: unknown, userEmail?: string | null): boolean {
  if (!item || typeof item !== 'object') return false;
  const isGuestSession = !normalizeRunningHubHistoryOwnerEmail(userEmail);
  const expected = getRunningHubHistoryOwnerScope(userEmail);
  const raw = (item as Record<string, unknown>)[RUNNINGHUB_HISTORY_OWNER_EMAIL_KEY];

  if (typeof raw !== 'string' || !raw.trim()) {
    return false;
  }

  const ownerLower = raw.trim().toLowerCase();
  const ownerNorm = normalizeRunningHubHistoryOwnerEmail(raw);
  const ownerResolved =
    ownerNorm ?? (ownerLower === RUNNINGHUB_HISTORY_GUEST_SCOPE ? RUNNINGHUB_HISTORY_GUEST_SCOPE : null);

  if (!ownerResolved) return false;

  if (!isGuestSession && ownerResolved === RUNNINGHUB_HISTORY_GUEST_SCOPE) {
    return false;
  }

  if (isGuestSession && ownerResolved !== RUNNINGHUB_HISTORY_GUEST_SCOPE) {
    return false;
  }

  return ownerResolved === expected;
}

export function filterRunningHubHistoryItemsForOwner<T>(
  items: T[],
  userEmail?: string | null,
): T[] {
  return items.filter((it) => runningHubHistoryItemOwnedBy(it, userEmail));
}

export function canSyncRunningHubHistoryItemToCloud(
  payload: unknown,
  userEmail: string | null | undefined,
  isAuthenticated: boolean,
): boolean {
  if (!isAuthenticated) return false;
  const email = normalizeRunningHubHistoryOwnerEmail(userEmail);
  if (!email) return false;
  return runningHubHistoryItemOwnedBy(payload, userEmail);
}

/** 只读 UI cache（登录用户：仅展示上次云端拉取结果，不作数据源） */
export function readRunningHubHistoryCacheFromLocalStorage<T extends { id: string }>(
  featureType: RunningHubHistoryFeatureType,
  userEmail?: string | null,
): T[] {
  const key = getRunningHubHistoryStorageKey(featureType, userEmail);
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return filterRunningHubHistoryItemsForOwner(parsed, userEmail) as T[];
  } catch {
    return [];
  }
}

/** guest 未登录：localStorage 为唯一来源 */
export function readGuestRunningHubHistoryFromLocalStorage<T extends { id: string }>(
  featureType: RunningHubHistoryFeatureType,
): T[] {
  return readRunningHubHistoryCacheFromLocalStorage<T>(featureType, null);
}

export function writeRunningHubHistoryListToLocalStorage<T extends Record<string, unknown>>(
  featureType: RunningHubHistoryFeatureType,
  userEmail: string | null | undefined,
  items: T[],
): void {
  const key = getRunningHubHistoryStorageKey(featureType, userEmail);
  const stamped = items
    .map((it) => stampRunningHubHistoryOwner(it, userEmail))
    .filter((it): it is T & { ownerEmail: string } => it !== null);
  try {
    localStorage.setItem(key, JSON.stringify(stripRunningHubHistoryListForLocalStorage(stamped)));
  } catch {
    /* ignore quota */
  }
}

export const RUNNINGHUB_HISTORY_RESET_EVENT = 'nemo-runninghub-history-reset';

export type RunningHubHistoryResetDetail = {
  /** 账号切换等场景：清空后由面板 hook 自动重拉云端 */
  reloadAfter?: boolean;
};

export function dispatchRunningHubHistoryReset(detail?: RunningHubHistoryResetDetail): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(RUNNINGHUB_HISTORY_RESET_EVENT, { detail }));
}

/** 与 reset 相同事件；App 账号切换时显式触发面板重载 */
export function dispatchRunningHubHistoryReload(): void {
  dispatchRunningHubHistoryReset({ reloadAfter: true });
}

export function subscribeRunningHubHistoryReset(
  onReset: (detail?: RunningHubHistoryResetDetail) => void,
): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = (e: Event) => onReset((e as CustomEvent<RunningHubHistoryResetDetail>).detail);
  window.addEventListener(RUNNINGHUB_HISTORY_RESET_EVENT, handler);
  return () => window.removeEventListener(RUNNINGHUB_HISTORY_RESET_EVENT, handler);
}
